import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { alpha, BottomNavigation, BottomNavigationAction, Box, Paper, styled } from '@mui/material';
import { m, useSpring } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { ReactComponent as CareIcon } from 'src/assets/icons/tabs/care.svg';
import { ReactComponent as HomeIcon } from 'src/assets/icons/tabs/home.svg';
import { ReactComponent as MyIcon } from 'src/assets/icons/tabs/my.svg';
import { ReactComponent as RecordIcon } from 'src/assets/icons/tabs/record.svg';
import { ReactComponent as ReportIcon } from 'src/assets/icons/tabs/report.svg';
import { FOOTER_HEIGHT, MOBILE_MAX_WIDTH } from 'src/assets/static/constants';
import { PATH_PAGE } from 'src/routes/paths';

// -----------------------------------------------------------------

interface TabItem {
  id: string;
  label: string;
  pathname: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
}

/**
 * Footer — 5탭 하단 네비게이션 (Curved Navigation Bar)
 *
 * 회의록 04:48 + W1 카탈로그 § 13 — 홈 / 기록 / 리포트 / 케어 / 마이
 *
 * 활성 탭은 sage green, 비활성은 warm gray.
 * Curved 효과: 활성 탭 위에 곡선 홈 + 떠있는 원형 버튼
 */
function Footer() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [value, setValue] = useState<number>(-1);
  const [prevValue, setPrevValue] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  // 컨테이너 실측 폭. 리사이징·새로고침·폰트 로딩 후 레이아웃 변경을 추적해
  // 떠있는 원(floatingX) 위치를 재계산하는 트리거.
  const [containerWidth, setContainerWidth] = useState<number>(0);
  // 측정이 일어날 때마다 증가하는 카운터. 폭(containerWidth)이 같은 값으로 다시
  // 측정돼 setState가 bail-out 되더라도 보정 effect가 반드시 다시 돌도록 하는 트리거.
  // (새로고침 시 ResizeObserver 초기 콜백이 mount 측정과 같은 폭을 보고해 보정이
  //  통째로 건너뛰던 버그 방지)
  const [measureTick, setMeasureTick] = useState<number>(0);

  const TABS: TabItem[] = useMemo(
    () => [
      { id: 'tab_home', label: '홈', pathname: PATH_PAGE.home, Icon: HomeIcon },
      { id: 'tab_report', label: '리포트', pathname: PATH_PAGE.report.root, Icon: ReportIcon },
      { id: 'tab_record', label: '기록', pathname: PATH_PAGE.record.root, Icon: RecordIcon },
      { id: 'tab_care', label: '케어', pathname: PATH_PAGE.care.root, Icon: CareIcon },
      { id: 'tab_my', label: '마이', pathname: PATH_PAGE.myPage.root, Icon: MyIcon },
    ],
    [],
  );

  useEffect(() => {
    const index = TABS.findIndex((tab) =>
      tab.pathname === PATH_PAGE.home ? pathname === '/' : pathname.startsWith(tab.pathname),
    );
    setValue(index);
  }, [pathname, TABS]);

  // 컨테이너 폭을 실측해 상태로 추적 — 새로고침/폰트로딩/리사이징 시 원 위치 재계산 트리거.
  // useEffect → useLayoutEffect: 페인트 이전에 측정해 어긋난 프레임이 화면에 보이지 않게 함.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // 측정 1회: 폭을 상태로 반영하고, 값이 같아도 보정이 돌도록 tick 증가.
    const update = () => {
      setContainerWidth(el.offsetWidth);
      setMeasureTick((t) => t + 1);
    };

    update(); // 마운트 직후 실측 (새로고침 시 초기 위치 보정)

    // 보조 안전장치: 더블 rAF로 레이아웃 확정 후 한 번 더 측정.
    // (새로고침 직후 스타일/레이아웃이 아직 안정화되기 전 측정값 보정)
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(update);
    });

    // 한글 웹폰트 로딩 완료 후 재측정 — 폰트 적용으로 레이아웃 폭이 바뀌면 재계산.
    let fontsCancelled = false;
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(() => {
        if (!fontsCancelled) update();
      });
    }

    // 네비 바 레이아웃 변화 자동 추적.
    const observer = new ResizeObserver(update);
    observer.observe(el);

    return () => {
      fontsCancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      observer.disconnect();
    };
  }, []);

  // 각 탭의 x 좌표 계산. 실측 상태폭 우선, 없으면 ref의 실시간 폭으로 폴백.
  const getTabX = useCallback(
    (index: number) => {
      if (index === -1) return 0;
      const width = containerWidth || containerRef.current?.offsetWidth || 0;
      if (!width) return 0;
      const tabWidth = width / TABS.length;
      return tabWidth * index + tabWidth / 2;
    },
    [containerWidth, TABS.length],
  );

  // 떠있는 원형 버튼의 x 좌표
  const floatingX = useSpring(getTabX(value), {
    stiffness: 250,
    damping: 25,
    mass: 0.8,
  });

  useEffect(() => {
    if (prevValue === -1 || value === -1) {
      setPrevValue(value);
      // 최초 위치(또는 활성탭 없음)는 애니메이션 없이 즉시 스냅.
      // .set()은 스프링이 0(좌측)에서 날아오므로 .jump()로 건너뛴다.
      // (서브화면에서 footer 언마운트 후 재진입 시 "왼쪽에서 날아오는" 버그 방지)
      // 단, 폭(containerWidth)이 아직 0이면 getTabX가 0을 반환하므로 좌측 끝으로 가버린다.
      // 이 경우 jump를 건너뛰고, 아래 [value, containerWidth] effect가 폭 확정 후 스냅한다.
      if (getTabX(value) > 0 || value === -1) {
        floatingX.jump(getTabX(value));
      }
      return;
    }

    // 중간 탭들을 거쳐서 이동
    const start = prevValue;
    const end = value;
    const direction = end > start ? 1 : -1;
    const steps = Math.abs(end - start);

    // 중간 탭들을 순차적으로 거쳐감
    let currentStep = 0;
    const stepDuration = 150; // 각 탭 간 이동 시간

    const moveToNextStep = () => {
      currentStep++;
      if (currentStep <= steps) {
        const nextIndex = start + direction * currentStep;
        floatingX.set(getTabX(nextIndex));

        if (currentStep < steps) {
          setTimeout(moveToNextStep, stepDuration);
        }
      }
    };

    moveToNextStep();
    setPrevValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // 새로고침/초기 진입·리사이징·폰트로딩 보정: 측정(measureTick)이 일어날 때마다
  // 현재 활성 탭 위치로 애니메이션 없이 즉시 스냅.
  // 새로고침 시 폭이 0인 채로 좌측 끝(0)에 스냅됐다가, 폭이 실측되는 순간 이 effect가
  // 돌아 올바른 위치로 보정한다. measureTick에 의존 — 폭 값이 같아도(ResizeObserver
  // 초기 콜백 등) 반드시 다시 돌아 보정이 누락되지 않는다. 또한 useLayoutEffect로
  // 페인트 전에 스냅해 어긋난 위치가 화면에 보이지 않게 한다.
  // 탭 클릭 전환(value 변경) 애니메이션은 위 effect가 담당하므로 여기 의존성에서 제외한다.
  useLayoutEffect(() => {
    if (value === -1 || !containerWidth) return;
    floatingX.jump(getTabX(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureTick, containerWidth]);

  return (
    <Container elevation={0}>
      <InnerBar ref={containerRef}>
        {/* 활성 탭 플로팅 원 */}
        {value >= 0 && (
          <FloatingCircle style={{ x: floatingX }}>
            {value === 2 ? (
              // 기록 메뉴 활성: 큰 원
              <RecordActiveInner>
                {(() => {
                  const Icon = TABS[value].Icon;
                  return <Icon width={22} height={22} />;
                })()}
              </RecordActiveInner>
            ) : (
              // 다른 메뉴 활성: 일반 원
              <CircleInner>
                {(() => {
                  const Icon = TABS[value].Icon;
                  return <Icon width={16} height={16} />;
                })()}
              </CircleInner>
            )}
          </FloatingCircle>
        )}

        {/* 기존 BottomNavigation */}
        <StyledBottomNavigation
          showLabels
          value={value}
          onChange={(_e, next) => setValue(next)}
          data-has-active={value >= 0}
        >
          {TABS.map((tab, index) => (
            <StyledBottomNavigationAction
              key={tab.id}
              label={tab.label}
              value={index}
              onClick={() => {
                if (pathname !== tab.pathname) navigate(tab.pathname);
              }}
              icon={
                index === 2 ? (
                  <tab.Icon width={24} height={24} />
                ) : (
                  <tab.Icon width={16} height={16} />
                )
              }
              data-testid={tab.id}
              data-is-active={index === value}
              data-is-record={index === 2}
            />
          ))}
        </StyledBottomNavigation>
      </InnerBar>
    </Container>
  );
}
export default observer(Footer);

// ========== 스타일 컴포넌트 ==========

// 반응형 크기 계산 헬퍼
const getSizeConfig = () => {
  // Note: breakpoints.down()은 CSS media query string을 반환하므로 useMediaQuery 사용 권장
  // 여기서는 간단히 상수로 처리 (실제 breakpoint 체크는 런타임에 자동 적용)
  const baseConfig = {
    floatingCircleSize: 36,
    recordCircleSize: 46,
    recordOuterSize: 54,
    circleBackgroundSize: 54, // 70 → 66 → 62 → 58 → 54 (여백 축소)
    recordInnerCircle: 43,
    floatingTop: -8, // -20 → -18 → -16 → -12 → -8 (여백 축소)
    iconSize: {
      normal: 16,
      record: 24,
      recordActive: 22,
    },
    recordIconTranslateY: -3, // -12 → -11 → -8 → -10 → -7 → -5 → -3 (여백 축소)
    recordLabelTranslateY: -1, // -3 → -2 → -1 (여백 축소)
    recordSelectedLabelTranslateY: 3, // 활성 시 라벨을 아래로 내려 하이라이팅 원과 겹침 방지
    recordCircleTop: -5, // -13 → -12 → -9 → -5 (여백 축소)
    activeIconTranslateY: -3, // -8 → -7 → -5 → -3 (여백 축소)
    labelFontSize: 11,
    labelMarginTop: 5, // 4 → 3 → 5 (여백 축소)
    selectedLabelTranslateY: 3, // 2 → 1 → 3 (여백 축소)
  };

  // sm 이하에서 작은 크기 적용 (CSS로 처리)
  return {
    ...baseConfig,
    smallConfig: {
      floatingCircleSize: 32,
      recordCircleSize: 42,
      recordOuterSize: 50,
      circleBackgroundSize: 50, // 66 → 62 → 58 → 54 → 50 (여백 축소)
      recordInnerCircle: 39,
      floatingTop: -6, // -18 → -16 → -14 → -10 → -6 (여백 축소)
      iconSize: {
        normal: 14,
        record: 22,
        recordActive: 20,
      },
      recordIconTranslateY: -1, // -10 → -9 → -6 → -8 → -5 → -3 → -1 (여백 축소)
      recordLabelTranslateY: -1,
      recordSelectedLabelTranslateY: 2, // 활성 시 라벨을 아래로 내려 하이라이팅 원과 겹침 방지
      recordCircleTop: -3, // -11 → -10 → -7 → -3 (여백 축소)
      activeIconTranslateY: -2, // -7 → -6 → -4 → -2 (여백 축소)
      labelFontSize: 10,
      labelMarginTop: 4,
      selectedLabelTranslateY: 2,
    },
  };
};

// 흰 바는 화면 전체 폭(풀폭), 탭/플로팅 원은 InnerBar가 768로 가운데 정렬
const Container = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  width: '100%',
  height: FOOTER_HEIGHT,
  zIndex: 50,
  overflow: 'visible',
  backgroundColor: theme.palette.background.paper,
  // 풀폭 바 상단의 옅은 그림자 (기존 nav 그림자를 여기로 이동)
  boxShadow: `0 ${theme.spacing(-0.25)} ${theme.spacing(1)} ${alpha(
    theme.palette.grey[500],
    0.08,
  )}`,
}));

// 탭 콘텐츠 가운데 정렬 칼럼 (모바일 폭). 탭 위치/플로팅 원 계산 기준.
const InnerBar = styled('div')({
  position: 'relative',
  width: '100%',
  maxWidth: MOBILE_MAX_WIDTH,
  height: '100%',
  margin: '0 auto',
  overflow: 'visible',
});

// 활성 탭 플로팅 원 (크기 동적)
const FloatingCircle = styled(m.div)(({ theme }) => {
  const config = getSizeConfig();
  return {
    position: 'absolute',
    top: config.floatingTop,
    width: config.recordOuterSize,
    height: config.recordOuterSize,
    marginLeft: -config.recordOuterSize / 2,
    zIndex: 3,
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      top: config.smallConfig.floatingTop,
      width: config.smallConfig.recordOuterSize,
      height: config.smallConfig.recordOuterSize,
      marginLeft: -config.smallConfig.recordOuterSize / 2,
    },
  };
});

const CircleInner = styled(Box)(({ theme }) => {
  const config = getSizeConfig();
  return {
    transition: 'all 0.2s ease',
    width: config.floatingCircleSize,
    height: config.floatingCircleSize,
    borderRadius: '50%',
    backgroundColor: alpha(theme.palette.primary.main, 0.9),
    boxShadow: `0 ${theme.spacing(0.5)} ${theme.spacing(1.5)} ${alpha(
      theme.palette.primary.main,
      0.3,
    )}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.contrastText,
    '& svg': {
      filter: `drop-shadow(0 ${theme.spacing(0.25)} ${theme.spacing(0.5)} rgba(0,0,0,0.1))`,
    },
    [theme.breakpoints.down('sm')]: {
      width: config.smallConfig.floatingCircleSize,
      height: config.smallConfig.floatingCircleSize,
    },
  };
});

const RecordActiveInner = styled(Box)(({ theme }) => {
  const config = getSizeConfig();
  return {
    width: config.recordCircleSize,
    height: config.recordCircleSize,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    boxShadow: `0 ${theme.spacing(0.75)} ${theme.spacing(2)} ${alpha(
      theme.palette.primary.main,
      0.4,
    )}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.contrastText,
    '& svg': {
      filter: `drop-shadow(0 ${theme.spacing(0.25)} ${theme.spacing(0.5)} rgba(0,0,0,0.15))`,
    },
    [theme.breakpoints.down('sm')]: {
      width: config.smallConfig.recordCircleSize,
      height: config.smallConfig.recordCircleSize,
    },
  };
});

const StyledBottomNavigation = styled(BottomNavigation)(({ theme }) => {
  const config = getSizeConfig();
  return {
    height: FOOTER_HEIGHT,
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
    zIndex: 2,
    // 그림자는 풀폭 Container가 담당 (768 경계에 그림자선이 생기지 않도록)
    boxShadow: 'none',

    // 가운데 원이 절반 겹쳐진 모양 (기록 메뉴 위치)
    '&::before': {
      content: '""',
      position: 'absolute',
      top: config.floatingTop,
      left: '50%',
      transform: 'translateX(-50%)',
      width: config.circleBackgroundSize,
      height: config.circleBackgroundSize,
      backgroundColor: theme.palette.background.paper,
      borderRadius: '50%',
      boxShadow: `0 ${theme.spacing(-0.25)} ${theme.spacing(1)} ${alpha(
        theme.palette.grey[500],
        0.08,
      )}`,
      zIndex: -1,
      [theme.breakpoints.down('sm')]: {
        top: config.smallConfig.floatingTop,
        width: config.smallConfig.circleBackgroundSize,
        height: config.smallConfig.circleBackgroundSize,
      },
    },
  };
});

const StyledBottomNavigationAction = styled(BottomNavigationAction)(({ theme }) => {
  const config = getSizeConfig();
  return {
    minWidth: 0,
    color: theme.palette.grey[500],
    transition: 'all 0.3s',
    // 기록 메뉴 스타일
    // 주의: 활성(Mui-selected) 시 아래 '&&.Mui-selected' 규칙이 라벨/아이콘 transform을
    // 더 높은 specificity로 덮어쓴다. 기록 탭은 아이콘이 원으로 위에 떠 있으므로,
    // 활성 상태까지 specificity를 맞춰(&&[data-is-record]) 라벨이 아래로 떨어지지 않게 고정한다.
    '&&[data-is-record="true"]': {
      '& svg': {
        color: theme.palette.primary.main,
        transform: `translateY(${config.recordIconTranslateY}px)`,
        [theme.breakpoints.down('sm')]: {
          transform: `translateY(${config.smallConfig.recordIconTranslateY}px)`,
        },
      },
      '& .MuiBottomNavigationAction-label': {
        transform: `translateY(${config.recordLabelTranslateY}px)`,
        [theme.breakpoints.down('sm')]: {
          transform: `translateY(${config.smallConfig.recordLabelTranslateY}px)`,
        },
      },
      // 기록 아이콘 뒤 연한 연두색 원형 배경 — 활성/비활성 무관하게 기록 탭이면 항상 표시.
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '-2px', // config.recordCircleTop,
        left: '50%',
        transform: 'translateX(-50%)',
        width: config.recordInnerCircle,
        height: config.recordInnerCircle,
        backgroundColor: alpha(theme.palette.primary.light, 0.2),
        borderRadius: '50%',
        boxShadow: `0 ${theme.spacing(-0.25)} ${theme.spacing(1)} ${alpha(
          theme.palette.grey[500],
          0.08,
        )}`,
        zIndex: -1,
        [theme.breakpoints.down('sm')]: {
          top: config.smallConfig.recordCircleTop,
          width: config.smallConfig.recordInnerCircle,
          height: config.smallConfig.recordInnerCircle,
        },
      },
    },
    // 활성 기록 탭: 아이콘은 원 위치에 맞춰 위로, 라벨도 아이콘을 따라 올려 간격 고정.
    // (일반 활성 탭의 라벨 translateY(+3) 하강을 기록 탭에서는 상쇄)
    '&&[data-is-record="true"].Mui-selected': {
      '& svg': {
        transform: `translateY(${config.recordIconTranslateY}px) scale(1.1)`,
        [theme.breakpoints.down('sm')]: {
          transform: `translateY(${config.smallConfig.recordIconTranslateY}px) scale(1.1)`,
        },
      },
      // 활성 시 라벨을 아래로 내려 하이라이팅 원과 겹치지 않게 한다.
      '& .MuiBottomNavigationAction-label': {
        transform: `translateY(${config.recordSelectedLabelTranslateY}px) scale(1.1)`,
        [theme.breakpoints.down('sm')]: {
          transform: `translateY(${config.smallConfig.recordSelectedLabelTranslateY}px) scale(1.1)`,
        },
      },
    },

    // 기본 label 스타일
    '& .MuiBottomNavigationAction-label': {
      fontSize: config.labelFontSize,
      fontWeight: 400,
      marginTop: config.labelMarginTop,
      opacity: 1,
      transition: 'all 0.3s',
      [theme.breakpoints.down('sm')]: {
        fontSize: config.smallConfig.labelFontSize,
        marginTop: config.smallConfig.labelMarginTop,
      },
    },

    // 활성 탭 스타일
    '&&.Mui-selected': {
      color: theme.palette.primary.main,
      '& svg': {
        transition: 'all 0.3s',
        zIndex: 1,
        transform: `translateY(${config.activeIconTranslateY}px) scale(1.1)`,
        color: theme.palette.primary.contrastText,
        [theme.breakpoints.down('sm')]: {
          transform: `translateY(${config.smallConfig.activeIconTranslateY}px) scale(1.1)`,
        },
      },
      '& .MuiBottomNavigationAction-label': {
        fontSize: config.labelFontSize,
        fontWeight: 800,
        transform: `translateY(${config.selectedLabelTranslateY}px) scale(1.1)`,
        [theme.breakpoints.down('sm')]: {
          fontSize: config.smallConfig.labelFontSize,
          transform: `translateY(${config.smallConfig.selectedLabelTranslateY}px) scale(1.1)`,
        },
      },
    },
  };
});
